/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap-v5'
import * as Yup from 'yup'
import { BankModel } from '../../../models/StoreModel'
import { ModalModel } from '../../../models/CommonModel'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import clsx from 'clsx'
import * as StoreCRUD from '../StoreCRUD'
import { Confirm, removeTrailingZeros } from '../../../modules/common/helper'
import { useIntl } from 'react-intl'
import { NumericFormat } from 'react-number-format'
import { revertPriceNumner } from '../../../selectors'

type Props = {
    show: boolean
    mode: string
    data?: any
    banks: BankModel[]
    onClose: () => void
    handleSaveBank: (values: FormData, action: string) => void
    handleRemoveBank: (id: number) => void
}

const addSchema = Yup.object().shape({
    bank_name: Yup.string().required('Vui lòng nhập tên ngân hàng'),
    bank_branch: Yup.string().required('Vui lòng nhập chi nhánh'),
    bank_account_name: Yup.string().required('Vui lòng nhập chủ tài khoản'),
    bank_account_number: Yup.string().required('Vui lòng nhập số tài khoản')
})

const BankListModal: React.FC<Props> = ({ show, mode, banks, onClose, handleSaveBank, handleRemoveBank }) => {
  const addInitValues = {
    bank_name: '',
    bank_branch: '',
    bank_account_name: '',
    bank_account_number: '',
    is_default: 0,
    opening_amount: 0
  }
  const [cMode, setCMode] = useState<string>(mode)
  const [qrImage, setQrImage] = useState<string | ArrayBuffer | null>(null);
  const [initValues, setInitValues] = useState<any>(addInitValues);
  const intl = useIntl();

  const handleAddBank = () => {
    setInitValues(addInitValues)
    setQrImage(null)
    setCMode('add')
  }

  const handleEditBank = (bank: BankModel) => {
    setInitValues(bank)
    setQrImage(bank.bank_qr_img ?? null)
    setCMode('edit')
  }

  const handleSubmit = async (values: any) => {
    var formData = new FormData();
    Object.entries(values).forEach(([key, value]: [string, any]) => {
      if (value !== null && value !== '') {
        formData.append(key, value);
      }
    });
    await handleSaveBank(formData, values?.id ? 'update' : 'add')
    setCMode('list')
  }

  const removeBank = async (id: number) => {
    Confirm.fire({
      icon: 'question',
      title: intl.formatMessage({ id: 'COMMON.CONFIRM_DELETE' }, { name: 'thông tin ngân hàng đã chọn' }),
      confirmButtonText: intl.formatMessage({ id: 'COMMON.YES' }),
      cancelButtonText: intl.formatMessage({ id: 'COMMON.NO' }),
    }).then(async (res) => {
      if (res.isConfirmed) {
        await handleRemoveBank(id)
        setCMode('list')
      }
    })
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header className='d-flex'>
        <Modal.Title>Tài khoản ngân hàng {cMode}</Modal.Title>
        {cMode !== 'list' && (
          <button 
            onClick={() => setCMode('list')}
            className='btn btn-icon btn-light-primary'>
            <i className='fa fa-angle-left'></i>
          </button>
        )}
        {cMode === 'list' && (
          <button 
            onClick={handleAddBank}
            className='btn btn-icon btn-light-primary'>
            <i className='fa fa-plus'></i>
          </button>
        )}
      </Modal.Header>
      <Modal.Body className="px-10">
        {cMode === 'list' ? (
          <>
            {banks && banks.map((bank: BankModel, index: number) => {
              return <div className='col-12 mb-3'>
                <div className='d-flex align-items-center'>
                  <div className=''>
                    <label
                      onClick={() => handleEditBank(bank)} 
                      className='form-label mb-0 cursor-pointer' htmlFor='bank_account_number'>
                      {index + 1}. {bank.bank_account_number}
                      {bank.is_default === 1 && (
                        <i className='ms-3 fa fa-check-circle text-success'></i>
                      )}
                    </label>
                    <p className='mb-0'>- Chủ thể: {bank.bank_account_name}</p>
                    <p className='mb-0'>- Ngân hàng: {bank.bank_name} - Chi nhánh: {bank.bank_branch}</p>
                  </div>
                  <div className='ms-auto d-flex align-items-center'>
                    {(bank.bank_qr_img !== null && bank.bank_qr_img !== 'null') && (
                      <div >
                        <img width={100} src={bank.bank_qr_img.toString()} alt="Preview" style={{ maxWidth: "100%" }} />
                      </div>
                    )}
                    <div className='ms-3'>
                      <button 
                        onClick={() => handleEditBank(bank)} 
                        className="btn btn-icon btn-light-warning w-30px h-30px ps-2 me-2">
                        <i className="bi bi-pencil-square fs-4 me-2"></i>
                      </button>
                      <button 
                        onClick={() => removeBank(bank.id)}
                        className="btn btn-icon btn-light-danger w-30px h-30px ps-2">
                        <i className="bi bi-trash fs-4 me-2"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            })}
          </>
        ) : (
          <>
            <Formik enableReinitialize validationSchema={addSchema} initialValues={initValues} onSubmit={handleSubmit}>
              {({ values, handleChange, setValues }) => (
                  <Form>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                          <label className="required form-label">Số tài khoản</label>
                          <Field
                              type='text'
                              className='form-control'
                              placeholder=''
                              autoComplete='nope'
                              name='bank_account_number'
                              required
                          />
                          <ErrorMessage name='bank_account_number' className='text-danger' component='span' />
                      </div>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                          <label className="required form-label">Chủ tài khoản</label>
                          <Field
                              type='text'
                              className='form-control'
                              placeholder=''
                              autoComplete='nope'
                              name='bank_account_name'
                              required
                          />
                          <ErrorMessage name='bank_account_name' className='text-danger' component='span' />
                      </div>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                          <label className="required form-label">Ngân hàng</label>
                          <Field
                              type='text'
                              className='form-control'
                              placeholder=''
                              autoComplete='nope'
                              name='bank_name'
                              required
                          />
                          <ErrorMessage name='bank_name' className='text-danger' component='span' />
                      </div>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                          <label className="required form-label">Chi nhánh</label>
                          <Field
                              type='text'
                              className='form-control'
                              placeholder=''
                              autoComplete='nope'
                              name='bank_branch'
                              required
                          />
                          <ErrorMessage name='bank_branch' className='text-danger' component='span' />
                      </div>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                          <label className="form-label">Qr Thanh toán</label>
                          <input
                              id='bank_qr'
                              name='bank_qr'
                              type='file'
                              value={undefined}
                              className={clsx(
                                  'form-control form-control-lg'
                              )}
                              placeholder='File ảnh QR'
                              autoComplete='off'
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  const file = event.currentTarget.files && event.currentTarget.files[0];
                                  if (file) {
                                      setValues({
                                          ...values,
                                          bank_qr: file
                                      });
                                      const reader = new FileReader();
                                      reader.onload = () => {
                                          setQrImage(reader.result);
                                      };
                                      reader.readAsDataURL(file);
                                  }
                              }}
                              />
                              {qrImage && (
                                  <div className='mt-3'>
                                      <img width={100} src={qrImage.toString()} alt="Preview" style={{ maxWidth: "100%" }} />
                                  </div>
                              )}
                      </div>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                        <label className="required form-label">Số đầu kỳ</label>
                        <NumericFormat
                          thousandSeparator=","
                          name='opening_amount'
                          className='form-control form-control-sm'
                          required
                          value={removeTrailingZeros(values.opening_amount ?? 0)}
                          onBlur={(e) => {
                              handleChange({target: {name: 'opening_amount', value: revertPriceNumner(e.target.value)}});
                          }}
                        />
                        <ErrorMessage name='opening_amount' className='text-danger' component='span' />
                    </div>
                      <div className="fv-row mb-3 flex-row-fluid fv-plugins-icon-container">
                          <div className="form-check">
                              <input
                                className="form-check-input" 
                                type="checkbox" 
                                id="is_default"
                                checked={values.is_default === 1}
                                onChange={(e) => {
                                  setValues({
                                    ...values,
                                    is_default: e.target.checked ? 1 : 0
                                  })
                                }}
                                name='is_default' />
                              <label htmlFor="is_default" className="form-check-label form-label mb-2">Tài khoản mặc định</label>
                          </div>
                      </div>
                      <div className='d-flex mb-4 justify-content-between pt-8'>
                          <button className="btn btn-bg-secondary" type="button" onClick={() => setCMode('list')}>Hủy</button>
                          <button className="btn btn-bg-success" type="submit">
                            Lưu
                          </button>
                      </div>
                  </Form>
              )}
            </Formik>
          </>
        )}
        
        
      </Modal.Body>
    </Modal>
  )
}

export default BankListModal